/**
 * Volume assembly — turns a series of decoded slices into one
 * 3-D voxel buffer ready for the ray-marcher.
 *
 * Spatial ordering:
 *   1. Load every instance (shared priority queue with the 2D viewer).
 *   2. Compute slice-normal = row × col (ImageOrientationPatient).
 *   3. Project each slice's ImagePositionPatient onto the normal →
 *      signed position along the acquisition axis.
 *   4. Sort ascending. Stable for ties.
 *
 * Spacing:
 *   x, y — PixelSpacing[1], PixelSpacing[0]
 *   z    — median |Δpos| between adjacent sorted slices (robust to the
 *          one-off missing-slice case).  Falls back to SliceThickness,
 *          then to 1 mm, so even pathological series still render.
 *
 * Signedness:
 *   WebGL2 sampler3D binds integer textures via R16I/R16UI. We always
 *   upload as R16UI for a uniform shader: Int16 slices get a +32 768
 *   bias before stacking; the `signed` flag tells the shader to undo
 *   it in `raw = float(sample) - 32768`.
 */
import type { StudyInstance } from "../api/client";
import type { DecodedPixels } from "../core/dicom";
import { getOrLoad } from "../viewport/stackCache";
import type { Volume, VoxelArray } from "./types";

const volumesBySeries = new Map<string, Volume>();

/** If a build is already in flight for a series, resolve to its
 *  promise instead of kicking off a second traversal. Matters when
 *  the user toggles 2D↔3D quickly while the series is loading. */
const inflight = new Map<string, Promise<Volume>>();

export function cachedVolume(seriesUid: string): Volume | undefined {
  return volumesBySeries.get(seriesUid);
}
export function clearVolume(seriesUid: string) {
  volumesBySeries.delete(seriesUid);
}
export function clearAllVolumes() {
  volumesBySeries.clear();
}

export interface BuildProgress {
  loaded: number;
  total:  number;
  phase:  "loading" | "assembling" | "done";
}

export function buildVolume(
  instances: StudyInstance[],
  onProgress?: (p: BuildProgress) => void,
): Promise<Volume> {
  if (!instances.length) return Promise.reject(new Error("no instances"));
  const seriesUid = instances[0].seriesUid;
  const cached = volumesBySeries.get(seriesUid);
  if (cached) { onProgress?.({ loaded: cached.depth, total: cached.depth, phase: "done" }); return Promise.resolve(cached); }
  const pending = inflight.get(seriesUid);
  if (pending) return pending;

  // Keep only instances from this series (safety against callers who
  // hand us the whole study).  Input already comes sorted but we
  // re-sort by IPP after loading — instance number is an imperfect
  // proxy, especially for multi-echo / gated cardiac acquisitions.
  const list = instances.filter((i) => i.seriesUid === seriesUid);

  const p = (async (): Promise<Volume> => {
    onProgress?.({ loaded: 0, total: list.length, phase: "loading" });

    // Load every slice with highest priority (0) — the user is
    // actively waiting for the 3D render.
    let loaded = 0;
    const decoded: DecodedPixels[] = await Promise.all(
      list.map(async (inst) => {
        const px = await getOrLoad(inst, 0);
        loaded++;
        onProgress?.({ loaded, total: list.length, phase: "loading" });
        return px;
      }),
    );

    onProgress?.({ loaded: list.length, total: list.length, phase: "assembling" });

    // All slices must share dimensions + pixel type or the volume
    // isn't meaningful. Caller can catch and show a friendly message.
    const W = decoded[0].width, H = decoded[0].height;
    for (const d of decoded) {
      if (d.width !== W || d.height !== H) {
        throw new Error(`series has mixed slice sizes (${d.width}×${d.height} vs ${W}×${H})`);
      }
    }

    // ------------------------------------------------------------------
    // Spatial sort — project IPP onto slice normal = row × col.
    // If every slice ships IPP+IOP we get a proper signed distance and
    // a correct z-axis.  If some are missing we fall back to the list
    // order (instance number) — pragmatic, mostly right for single-
    // phase CT and MR.
    // ------------------------------------------------------------------
    type Item = { px: DecodedPixels; pos: number; order: number };
    const iop = decoded[0].imageOrientationPatient;
    let normal: [number, number, number] | undefined;
    if (iop) {
      const rx = iop[0], ry = iop[1], rz = iop[2];
      const cx = iop[3], cy = iop[4], cz = iop[5];
      normal = [
        ry * cz - rz * cy,
        rz * cx - rx * cz,
        rx * cy - ry * cx,
      ];
    }
    const items: Item[] = decoded.map((px, i) => {
      let pos = i; // fallback to native order
      if (normal && px.imagePositionPatient) {
        pos = px.imagePositionPatient[0] * normal[0]
            + px.imagePositionPatient[1] * normal[1]
            + px.imagePositionPatient[2] * normal[2];
      }
      return { px, pos, order: i };
    });
    items.sort((a, b) => a.pos - b.pos || a.order - b.order);

    // ------------------------------------------------------------------
    // z-spacing — median of adjacent |Δpos|. Clamp to [0.1, 10] so a
    // misreported zero doesn't produce NaN normals in the camera.
    // ------------------------------------------------------------------
    let zSpacing = items[0].px.sliceThickness ?? 1;
    if (items.length > 1 && normal) {
      const deltas: number[] = [];
      for (let i = 1; i < items.length; i++) {
        deltas.push(Math.abs(items[i].pos - items[i - 1].pos));
      }
      deltas.sort((a, b) => a - b);
      const median = deltas[Math.floor(deltas.length / 2)] || zSpacing;
      if (median > 0) zSpacing = median;
    }
    if (!(zSpacing > 0) || zSpacing > 10) zSpacing = 1;

    // ------------------------------------------------------------------
    // Signedness — if any slice came back as Int16 we treat the whole
    // volume as signed. Bias at upload time (+32768) so we can use one
    // Uint16 path in the shader.
    // ------------------------------------------------------------------
    const signed = items.some((i) => i.px.stored instanceof Int16Array);
    const D = items.length;
    const first = items[0].px;
    const bitsStored = first.bitsStored;

    // Allocate the output buffer.  For 8-bit DICOM we keep 8-bit to
    // halve GPU memory.  16-bit signed and 16-bit unsigned both end
    // up as Uint16Array (signed gets biased).
    let data: VoxelArray;
    const voxels = W * H * D;
    if (first.stored instanceof Uint8Array && !signed) {
      data = new Uint8Array(voxels);
    } else {
      data = new Uint16Array(voxels);
    }

    let minStored = Infinity, maxStored = -Infinity;
    for (let z = 0; z < D; z++) {
      const src = items[z].px.stored;
      if (!src) continue;
      const base = z * W * H;
      if (data instanceof Uint16Array) {
        if (src instanceof Int16Array) {
          for (let i = 0; i < src.length; i++) {
            const v = src[i];
            data[base + i] = v + 32768;
            if (v < minStored) minStored = v;
            if (v > maxStored) maxStored = v;
          }
        } else {
          // Uint8/Uint16 → Uint16 (copy, widening automatic for Uint8)
          data.set(src, base);
          if (items[z].px.minStored < minStored) minStored = items[z].px.minStored;
          if (items[z].px.maxStored > maxStored) maxStored = items[z].px.maxStored;
        }
      } else {
        (data as Uint8Array).set(src as Uint8Array, base);
        if (items[z].px.minStored < minStored) minStored = items[z].px.minStored;
        if (items[z].px.maxStored > maxStored) maxStored = items[z].px.maxStored;
      }
    }

    const ps = first.pixelSpacing;
    const spacing: [number, number, number] = [
      ps ? ps[1] : 1, // column spacing
      ps ? ps[0] : 1, // row spacing
      zSpacing,
    ];

    const vol: Volume = {
      seriesUid,
      data,
      signed,
      width:  W,
      height: H,
      depth:  D,
      spacing,
      origin: first.imagePositionPatient,
      orientation: first.imageOrientationPatient,
      minStored: isFinite(minStored) ? minStored : 0,
      maxStored: isFinite(maxStored) ? maxStored : 255,
      slope:     first.rescaleSlope,
      intercept: first.rescaleIntercept,
      bitsStored,
      photometric: first.photometric,
      singleSlice: D < 2,
    };
    volumesBySeries.set(seriesUid, vol);
    onProgress?.({ loaded: D, total: D, phase: "done" });
    return vol;
  })();

  inflight.set(seriesUid, p);
  p.finally(() => inflight.delete(seriesUid));
  return p;
}
